import { supabase } from "@/integrations/supabase/client";
import type { SystemSetting, SettingFormData, SettingsGrouped, SettingCategory } from "@/types/settings";

// Transform from Supabase format to our app format
const transformSetting = (row: any): SystemSetting => ({
  id: row.id,
  key: row.key,
  value: row.value,
  dataType: row.data_type,
  category: row.category,
  description: row.description,
  isSensitive: row.is_sensitive,
  isRequired: row.is_required,
  isPublic: row.is_public,
  validationRules: row.validation_rules,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  updatedBy: row.updated_by,
});

// Transform from our app format to Supabase format
const transformToSupabase = (setting: SettingFormData) => ({
  key: setting.key,
  value: setting.value,
  data_type: setting.dataType,
  category: setting.category,
  description: setting.description || null,
  is_sensitive: setting.isSensitive || false,
  is_required: setting.isRequired || false,
  is_public: setting.isPublic || false,
  validation_rules: setting.validationRules || null,
});

// Fetch all system settings
export const fetchSystemSettings = async (): Promise<SystemSetting[]> => {
  try {
    const { data, error } = await supabase
      .from("system_settings")
      .select("*")
      .order("category", { ascending: true })
      .order("key", { ascending: true });

    if (error) throw error;
    return (data || []).map(transformSetting);
  } catch (error) {
    console.error("Error fetching system settings:", error);
    throw error;
  }
};

// Fetch settings by category
export const fetchSettingsByCategory = async (category: SettingCategory): Promise<SystemSetting[]> => {
  try {
    const { data, error } = await supabase
      .from("system_settings")
      .select("*")
      .eq("category", category)
      .order("key", { ascending: true });

    if (error) throw error;
    return (data || []).map(transformSetting);
  } catch (error) {
    console.error(`Error fetching settings for category ${category}:`, error);
    throw error;
  }
};

// Fetch a single setting by key
export const fetchSettingByKey = async (key: string): Promise<SystemSetting | null> => {
  try {
    const { data, error } = await supabase
      .from("system_settings")
      .select("*")
      .eq("key", key)
      .single();

    if (error) {
      if (error.code === "PGRST116") return null; // Not found
      throw error;
    }

    return data ? transformSetting(data) : null;
  } catch (error) {
    console.error(`Error fetching setting with key ${key}:`, error);
    throw error;
  }
};

// Get settings grouped by category
export const fetchSettingsGrouped = async (): Promise<SettingsGrouped> => {
  try {
    const settings = await fetchSystemSettings();
    const grouped: SettingsGrouped = {};

    settings.forEach((setting) => {
      if (!grouped[setting.category]) {
        grouped[setting.category] = [];
      }
      grouped[setting.category].push(setting);
    });

    return grouped;
  } catch (error) {
    console.error("Error fetching grouped settings:", error);
    throw error;
  }
};

// Create a new setting
export const createSystemSetting = async (
  setting: SettingFormData
): Promise<SystemSetting> => {
  try {
    // Get current user profile to set updated_by
    const { data: profile } = await supabase
      .from("profiles")
      .select("id")
      .eq("uid", (await supabase.auth.getUser()).data.user?.id)
      .single();

    if (!profile) {
      throw new Error("User profile not found");
    }

    const { data, error } = await supabase
      .from("system_settings")
      .insert({
        ...transformToSupabase(setting),
        updated_by: profile.id,
      })
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error("No data returned after insert");

    return transformSetting(data);
  } catch (error) {
    console.error("Error creating system setting:", error);
    throw error;
  }
};

// Update an existing setting
export const updateSystemSetting = async (
  id: number,
  setting: Partial<SettingFormData>
): Promise<SystemSetting> => {
  try {
    // Get current user profile to set updated_by
    const { data: profile } = await supabase
      .from("profiles")
      .select("id")
      .eq("uid", (await supabase.auth.getUser()).data.user?.id)
      .single();

    if (!profile) {
      throw new Error("User profile not found");
    }

    const updateData = {
      ...transformToSupabase(setting as SettingFormData),
      updated_by: profile.id,
    };

    // Remove undefined values
    Object.keys(updateData).forEach(key => {
      if (updateData[key as keyof typeof updateData] === undefined) {
        delete updateData[key as keyof typeof updateData];
      }
    });

    const { data, error } = await supabase
      .from("system_settings")
      .update(updateData)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error("No data returned after update");

    return transformSetting(data);
  } catch (error) {
    console.error("Error updating system setting:", error);
    throw error;
  }
};

// Update setting value by key (convenience method)
export const updateSettingValue = async (
  key: string,
  value: string
): Promise<SystemSetting> => {
  try {
    // Get current user profile to set updated_by
    const { data: profile } = await supabase
      .from("profiles")
      .select("id")
      .eq("uid", (await supabase.auth.getUser()).data.user?.id)
      .single();

    if (!profile) {
      throw new Error("User profile not found");
    }

    const { data, error } = await supabase
      .from("system_settings")
      .update({
        value,
        updated_by: profile.id,
      })
      .eq("key", key)
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error("Setting not found or no data returned");

    return transformSetting(data);
  } catch (error) {
    console.error(`Error updating setting value for key ${key}:`, error);
    throw error;
  }
};

// Delete a setting
export const deleteSystemSetting = async (id: number): Promise<void> => {
  try {
    const { error } = await supabase
      .from("system_settings")
      .delete()
      .eq("id", id);

    if (error) throw error;
  } catch (error) {
    console.error("Error deleting system setting:", error);
    throw error;
  }
};

// Update setting public status by key
export const updateSettingPublicStatus = async (
  key: string,
  isPublic: boolean
): Promise<SystemSetting> => {
  try {
    // Get current user profile to set updated_by
    const { data: profile } = await supabase
      .from("profiles")
      .select("id")
      .eq("uid", (await supabase.auth.getUser()).data.user?.id)
      .single();

    if (!profile) {
      throw new Error("User profile not found");
    }

    const { data, error } = await supabase
      .from("system_settings")
      .update({
        is_public: isPublic,
        updated_by: profile.id,
      })
      .eq("key", key)
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error("Setting not found or no data returned");

    return transformSetting(data);
  } catch (error) {
    console.error(`Error updating public status for setting ${key}:`, error);
    throw error;
  }
};

// Bulk update settings (useful for form submissions)
export const bulkUpdateSettings = async (
  updates: Array<{ key: string; value: string }>,
  publicUpdates?: Array<{ key: string; isPublic: boolean }>
): Promise<SystemSetting[]> => {
  try {
    // Get current user profile to set updated_by
    const { data: profile } = await supabase
      .from("profiles")
      .select("id")
      .eq("uid", (await supabase.auth.getUser()).data.user?.id)
      .single();

    if (!profile) {
      throw new Error("User profile not found");
    }

    const results: SystemSetting[] = [];

    // Update values
    for (const update of updates) {
      try {
        const result = await updateSettingValue(update.key, update.value);
        results.push(result);
      } catch (error) {
        console.error(`Failed to update setting ${update.key}:`, error);
        // Continue with other updates even if one fails
      }
    }

    // Update public status
    if (publicUpdates) {
      for (const update of publicUpdates) {
        try {
          const result = await updateSettingPublicStatus(update.key, update.isPublic);
          // Update the result in our array if we already have it
          const existingIndex = results.findIndex(r => r.key === update.key);
          if (existingIndex >= 0) {
            results[existingIndex] = result;
          } else {
            results.push(result);
          }
        } catch (error) {
          console.error(`Failed to update public status for ${update.key}:`, error);
          // Continue with other updates even if one fails
        }
      }
    }

    return results;
  } catch (error) {
    console.error("Error in bulk update settings:", error);
    throw error;
  }
};

// Utility functions for working with setting values

// Parse setting value based on data type
export const parseSettingValue = (setting: SystemSetting): any => {
  if (setting.value === null || setting.value === '') {
    return null;
  }

  switch (setting.dataType) {
    case 'boolean':
      return setting.value === 'true';
    case 'integer':
      return parseInt(setting.value, 10);
    case 'decimal':
      return parseFloat(setting.value);
    case 'json':
      try {
        return JSON.parse(setting.value);
      } catch {
        return setting.value;
      }
    case 'string':
    case 'encrypted':
    default:
      return setting.value;
  }
};

// Convert value to string for storage
export const stringifySettingValue = (value: any, dataType: string): string => {
  if (value === null || value === undefined) {
    return '';
  }

  switch (dataType) {
    case 'boolean':
      return String(Boolean(value));
    case 'integer':
    case 'decimal':
      return String(value);
    case 'json':
      return typeof value === 'string' ? value : JSON.stringify(value);
    case 'string':
    case 'encrypted':
    default:
      return String(value);
  }
};

// Get setting value by key with type parsing
export const getSettingValue = async (key: string): Promise<any> => {
  try {
    const setting = await fetchSettingByKey(key);
    if (!setting) {
      return null;
    }
    return parseSettingValue(setting);
  } catch (error) {
    console.error(`Error getting setting value for key ${key}:`, error);
    return null;
  }
};

// Fetch public system settings from the edge function
export const fetchPublicSystemSettings = async (
  category?: string,
  format: 'object' | 'array' = 'object'
): Promise<{
  data: any;
  meta: {
    count: number;
    categories: string[];
    format: string;
    timestamp: string;
  };
}> => {
  try {
    // Get the current session for authentication
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session?.access_token) {
      throw new Error('No authentication token available');
    }

    // Build the URL with query parameters
    const baseUrl = `${supabase.supabaseUrl}/functions/v1/system-settings`;
    const params = new URLSearchParams();
    
    if (category) {
      params.append('category', category);
    }
    if (format) {
      params.append('format', format);
    }
    
    const url = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;

    // Make the GET request to the edge function
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error fetching public system settings:', error);
    throw error;
  }
};

// Validate setting value against its validation rules
export const validateSettingValue = (setting: SystemSetting, value: any): string[] => {
  const errors: string[] = [];

  // Check if required
  if (setting.isRequired && (value === null || value === undefined || value === '')) {
    errors.push(`${setting.key} is required`);
    return errors;
  }

  // Skip validation if value is empty and not required
  if (!setting.isRequired && (value === null || value === undefined || value === '')) {
    return errors;
  }

  // Type-specific validation
  switch (setting.dataType) {
    case 'integer':
      if (isNaN(parseInt(value))) {
        errors.push(`${setting.key} must be a valid integer`);
      }
      break;
    case 'decimal':
      if (isNaN(parseFloat(value))) {
        errors.push(`${setting.key} must be a valid number`);
      }
      break;
    case 'boolean':
      if (typeof value !== 'boolean' && value !== 'true' && value !== 'false') {
        errors.push(`${setting.key} must be true or false`);
      }
      break;
    case 'json':
      if (typeof value === 'string') {
        try {
          JSON.parse(value);
        } catch {
          errors.push(`${setting.key} must be valid JSON`);
        }
      }
      break;
  }

  // Custom validation rules
  if (setting.validationRules) {
    // Add custom validation logic here based on validation_rules
    // This could be extended to support JSON schema validation
  }

  return errors;
}; 